import type {
  InboxV2Account,
  InboxV2Conversation,
  InboxV2MessageType,
  InboxV2Sentiment,
} from "../types";
import type {
  Conversation,
  MentionConversation,
} from "../../types/conversationInbox";
import {
  isInboxV2InternalLikeMessage,
  isInboxV2SystemMessage,
} from "./message-meta";

export type InboxV2ReplyComposerSupport = true | "internal-only";

export interface InboxV2ConversationLayoutConfig {
  notificationText?: string;
  showExpiredReplyWarning: boolean;
  socialReplyDisabledMessage?: string;
  supportsLoveReaction: boolean;
}

export type InboxV2ConversationKind =
  | "comment-thread"
  | "direct-message"
  | "tagged-in-media"
  | "post"
  | "review"
  | "qa";

export type InboxV2ConversationUiLayout =
  "comment" | "contextual" | "direct-message" | "review";

export type InboxV2TaggedConversationSubtype =
  "mention-in-comment" | "mention-in-caption" | "tagged-post";

type LegacyTaggedConversation = Pick<Conversation, "type" | "post"> & {
  instagram?: Partial<NonNullable<MentionConversation["instagram"]>>;
  facebook?: Partial<NonNullable<MentionConversation["facebook"]>>;
};

interface InboxV2ConversationDefinition {
  label: string;
  feedLabel: string;
  kind: InboxV2ConversationKind;
  layout: InboxV2ConversationUiLayout;
  supportsAllCommentsPanel?: boolean;
  supportsLoveReaction?: boolean;
  supportsPostCard?: boolean;
  supportsReplyComposer?:
    | InboxV2ReplyComposerSupport
    | ((
        conversation: Pick<InboxV2Conversation, "messageType" | "platform"> &
          Partial<Pick<InboxV2Conversation, "messages">>,
        account?: Pick<InboxV2Account, "platform">
      ) => InboxV2ReplyComposerSupport | undefined);
  getSocialReplyDisabledMessage?: (
    platform: InboxV2Conversation["platform"]
  ) => string | undefined;
}

export const disableReplyForIgTaggedPostMessage =
  "To comment on this post or remove the tag, please visit Instagram directly. You can still make internal comments.";

const inboxV2UnsupportedConversationTypes = new Set([
  "MENTION",
  "QA",
  "COMMENT",
]);

export function isInboxV2UnsupportedConversation(
  conversation: Pick<InboxV2Conversation, "sourceConversationType">
) {
  return (
    conversation.sourceConversationType !== undefined &&
    inboxV2UnsupportedConversationTypes.has(conversation.sourceConversationType)
  );
}

function getIgTaggedPostSocialReplyDisabledMessage(
  platform: InboxV2Conversation["platform"]
) {
  return platform === "instagram"
    ? disableReplyForIgTaggedPostMessage
    : undefined;
}

function getIgTaggedPostReplyComposerSupport(
  conversation: Pick<InboxV2Conversation, "platform">,
  account?: Pick<InboxV2Account, "platform">
) {
  const platform = account?.platform ?? conversation.platform;
  return platform === "instagram" ? "internal-only" : true;
}

function getReviewReplyComposerSupport(
  conversation: Partial<Pick<InboxV2Conversation, "messages">>
) {
  return conversation.messages?.some(
    (message) =>
      message.kind === "gmb-review-reply" &&
      (!("isDeleted" in message) || !message.isDeleted)
  )
    ? "internal-only"
    : true;
}

const inboxV2ConversationDefinitions: Record<
  Exclude<InboxV2MessageType, "all">,
  InboxV2ConversationDefinition
> = {
  comment: {
    label: "Comment",
    feedLabel: "Post comments",
    kind: "comment-thread",
    layout: "comment",
    supportsAllCommentsPanel: true,
    supportsLoveReaction: true,
    supportsPostCard: true,
    supportsReplyComposer: true,
  },
  "threaded-comment": {
    label: "Comment",
    feedLabel: "Post comments",
    kind: "comment-thread",
    layout: "comment",
    supportsAllCommentsPanel: true,
    supportsLoveReaction: true,
    supportsPostCard: true,
    supportsReplyComposer: true,
  },
  dm: {
    label: "Direct Message",
    feedLabel: "Direct messages",
    kind: "direct-message",
    layout: "direct-message",
    supportsLoveReaction: true,
    supportsReplyComposer: true,
  },
  mention: {
    label: "Tagged",
    feedLabel: "Mentioned in comments",
    kind: "tagged-in-media",
    layout: "comment",
    supportsPostCard: true,
    supportsReplyComposer: true,
  },
  "mention-in-caption": {
    label: "Tagged",
    feedLabel: "Mentioned in caption",
    kind: "tagged-in-media",
    layout: "comment",
    supportsPostCard: true,
    supportsReplyComposer: true,
  },
  post: {
    label: "Post",
    feedLabel: "Posts on your page",
    kind: "post",
    layout: "contextual",
    supportsPostCard: true,
    supportsReplyComposer: true,
  },
  "tagged-post": {
    label: "Tagged",
    feedLabel: "Tagged in posts",
    kind: "tagged-in-media",
    layout: "comment",
    supportsPostCard: true,
    supportsReplyComposer: getIgTaggedPostReplyComposerSupport,
    getSocialReplyDisabledMessage: getIgTaggedPostSocialReplyDisabledMessage,
  },
  "tagged-in-media": {
    label: "Tagged",
    feedLabel: "Tagged in posts",
    kind: "tagged-in-media",
    layout: "comment",
    supportsPostCard: true,
    supportsReplyComposer: getIgTaggedPostReplyComposerSupport,
    getSocialReplyDisabledMessage: getIgTaggedPostSocialReplyDisabledMessage,
  },
  review: {
    label: "Review",
    feedLabel: "Reviews",
    kind: "review",
    layout: "review",
    supportsReplyComposer: getReviewReplyComposerSupport,
  },
  qa: {
    label: "Question",
    feedLabel: "Questions",
    kind: "qa",
    layout: "contextual",
    supportsReplyComposer: true,
  },
};

/**
 * Mirrors Inbox v1's social mention/tagged split.
 *
 * The public feed filter still exposes one "Tagged in posts" option, but the
 * rendered conversation needs the more precise subtype:
 * - TAGGED_POST means the account was tagged in media/post.
 * - MENTION_V2 with instagram.commentId or facebook.commentId means mentioned
 *   in a comment.
 * - MENTION_V2 without a commentId means mentioned in a post caption.
 * - Legacy MENTION falls back to the old post type heuristic.
 */
export function resolveInboxV2TaggedConversationSubtype(
  conversation: LegacyTaggedConversation
): InboxV2TaggedConversationSubtype | undefined {
  if (conversation.type === "TAGGED_POST") {
    return "tagged-post";
  }

  if (conversation.type === "MENTION_V2") {
    return conversation.instagram?.commentId || conversation.facebook?.commentId
      ? "mention-in-comment"
      : "mention-in-caption";
  }

  if (conversation.type === "MENTION") {
    return conversation.post?.type === "igCaptionPost"
      ? "mention-in-caption"
      : "mention-in-comment";
  }

  return undefined;
}

export function mapInboxV2TaggedSubtypeToMessageType(
  subtype: InboxV2TaggedConversationSubtype
): Exclude<InboxV2MessageType, "all"> {
  if (subtype === "mention-in-comment") {
    return "mention";
  }

  if (subtype === "mention-in-caption") {
    return "mention-in-caption";
  }

  return "tagged-post";
}

export const inboxV2ConversationTypeLabels: Record<
  Exclude<InboxV2MessageType, "all">,
  string
> = Object.fromEntries(
  Object.entries(inboxV2ConversationDefinitions).map(([type, definition]) => [
    type,
    definition.label,
  ])
) as Record<Exclude<InboxV2MessageType, "all">, string>;

const inboxV2UserFacingConversationTypes = [
  "dm",
  "comment",
  "post",
  "tagged-post",
  "review",
] as const satisfies ReadonlyArray<Exclude<InboxV2MessageType, "all">>;

export const inboxV2ConversationTypeOptions =
  inboxV2UserFacingConversationTypes.map((value) => {
    const definition = inboxV2ConversationDefinitions[value];

    return {
      value,
      label: definition.feedLabel,
    };
  }) satisfies ReadonlyArray<{
    value: Exclude<InboxV2MessageType, "all">;
    label: string;
  }>;

export const inboxV2ConversationTypeAliasOptions = (
  Object.entries(inboxV2ConversationDefinitions) as Array<
    [Exclude<InboxV2MessageType, "all">, InboxV2ConversationDefinition]
  >
).map(([value, definition]) => ({
  value,
  label: definition.feedLabel,
})) satisfies ReadonlyArray<{
  value: Exclude<InboxV2MessageType, "all">;
  label: string;
}>;

export const inboxV2FeedConversationTypeOptions = [
  ...inboxV2ConversationTypeOptions,
  { value: "all", label: "All message types" },
] as const;

export const inboxV2SentimentLabels: Record<InboxV2Sentiment, string> = {
  positive: "Positive",
  negative: "Negative",
  neutral: "Neutral",
  mixed: "Mixed",
  unknown: "Unknown",
};

export const inboxV2SentimentOptions = [
  { value: "positive", label: "Positive" },
  { value: "neutral", label: "Neutral" },
  { value: "negative", label: "Negative" },
  { value: "mixed", label: "Mixed" },
  { value: "unknown", label: "Unknown" },
] as const satisfies ReadonlyArray<{
  label: string;
  value: InboxV2Sentiment;
}>;

export const inboxV2FeedSentimentOptions = [
  ...inboxV2SentimentOptions,
  { value: "all", label: "All sentiments" },
] as const;

export function getInboxV2ConversationDefinition(
  messageType: InboxV2Conversation["messageType"]
) {
  return inboxV2ConversationDefinitions[messageType];
}

export function getInboxV2ConversationTypeLabel(
  messageType: InboxV2Conversation["messageType"]
) {
  return getInboxV2ConversationDefinition(messageType).label;
}

export function getInboxV2SourceNotificationText(
  conversation: Pick<
    InboxV2Conversation,
    "customerName" | "messageType" | "sourceContext"
  >
) {
  const authorName =
    conversation.sourceContext?.authorName ??
    conversation.customerName ??
    "Someone";

  switch (conversation.sourceContext?.previewIn) {
    case "comment":
      return `${authorName} commented on your post.`;
    case "mention-in-comment":
      return `${authorName} mentioned you in their comment.`;
    case "mention-in-caption":
      return `${authorName} mentioned you in their post.`;
    case "new-post-on-page":
      return `${authorName} made a new post on your page.`;
    case "tagged-in-post":
      return `${authorName} tagged you in a post.`;
    default:
      if (conversation.messageType === "mention-in-caption") {
        return `${authorName} mentioned you in their post.`;
      }

      if (
        conversation.messageType === "mention" ||
        conversation.messageType === "tagged-post" ||
        conversation.messageType === "tagged-in-media"
      ) {
        return `${authorName} tagged you in a post.`;
      }

      if (conversation.messageType === "post") {
        return `${authorName} made a new post on your page.`;
      }

      return undefined;
  }
}

export function matchesInboxV2ConversationTypeFilter(
  conversationType: InboxV2Conversation["messageType"],
  filterType: InboxV2MessageType
) {
  if (filterType === "all") {
    return true;
  }

  const filterDefinition = inboxV2ConversationDefinitions[filterType];
  const conversationDefinition =
    getInboxV2ConversationDefinition(conversationType);

  return conversationDefinition.kind === filterDefinition.kind;
}

export function isInboxV2DirectMessageType(
  messageType: InboxV2Conversation["messageType"]
) {
  return (
    getInboxV2ConversationDefinition(messageType).kind === "direct-message"
  );
}

export function isInboxV2CommentConversationType(
  messageType: InboxV2Conversation["messageType"]
) {
  return (
    getInboxV2ConversationDefinition(messageType).kind === "comment-thread"
  );
}

export function supportsInboxV2AllCommentsPanel(
  messageType: InboxV2Conversation["messageType"]
) {
  return Boolean(
    getInboxV2ConversationDefinition(messageType).supportsAllCommentsPanel
  );
}

export function supportsInboxV2LoveReaction(
  conversation: Pick<InboxV2Conversation, "messageType" | "platform">,
  account?: Pick<InboxV2Account, "platform">
) {
  const platform = account?.platform ?? conversation.platform;
  const definition = getInboxV2ConversationDefinition(conversation.messageType);

  if (!definition.supportsLoveReaction) {
    return false;
  }

  if (definition.kind === "direct-message") {
    return platform === "instagram";
  }

  if (definition.kind === "comment-thread") {
    return platform === "tiktok";
  }

  return false;
}

export function isInboxV2ConversationReplyExpired(
  conversation: Pick<InboxV2Conversation, "lastMessageAt" | "messageType"> &
    Partial<Pick<InboxV2Conversation, "messages">>
) {
  if (!isInboxV2DirectMessageType(conversation.messageType)) {
    return false;
  }

  const latestSocialMessage = conversation.messages?.findLast(
    (message) =>
      !isInboxV2SystemMessage(message) && !isInboxV2InternalLikeMessage(message)
  );
  const lastMessageTime = new Date(
    latestSocialMessage?.createdAt ?? conversation.lastMessageAt
  ).getTime();

  if (Number.isNaN(lastMessageTime)) {
    return false;
  }

  return Date.now() - lastMessageTime > 7 * 24 * 60 * 60 * 1000;
}

export function getInboxV2SocialReplyDisabledMessage(
  conversation: Pick<InboxV2Conversation, "messageType" | "platform">,
  account?: Pick<InboxV2Account, "platform">
) {
  const platform = account?.platform ?? conversation.platform;
  return getInboxV2ConversationDefinition(
    conversation.messageType
  ).getSocialReplyDisabledMessage?.(platform);
}

export function getInboxV2ReplyComposerSupport(
  conversation: Pick<InboxV2Conversation, "messageType" | "platform"> &
    Partial<Pick<InboxV2Conversation, "messages">>,
  account?: Pick<InboxV2Account, "platform">
) {
  const support = getInboxV2ConversationDefinition(
    conversation.messageType
  ).supportsReplyComposer;

  if (typeof support === "function") {
    return support(conversation, account);
  }

  return support;
}

export function getInboxV2ConversationLayoutConfig(
  conversation: Pick<
    InboxV2Conversation,
    | "customerName"
    | "lastMessageAt"
    | "messageType"
    | "platform"
    | "sourceContext"
  > &
    Partial<Pick<InboxV2Conversation, "messages">>,
  account?: Pick<InboxV2Account, "platform">
): InboxV2ConversationLayoutConfig {
  const definition = getInboxV2ConversationDefinition(conversation.messageType);
  const notificationText =
    definition.kind !== "comment-thread" && definition.kind !== "direct-message"
      ? (conversation.sourceContext?.notificationText ??
        getInboxV2SourceNotificationText(conversation))
      : undefined;

  return {
    notificationText,
    showExpiredReplyWarning: isInboxV2ConversationReplyExpired(conversation),
    socialReplyDisabledMessage: getInboxV2SocialReplyDisabledMessage(
      conversation,
      account
    ),
    supportsLoveReaction: supportsInboxV2LoveReaction(conversation, account),
  };
}

export function supportsInboxV2TaggedContentActions(
  conversation: Pick<
    InboxV2Conversation,
    "messageType" | "platform" | "sourceContext"
  >,
  account?: Pick<InboxV2Account, "platform">
) {
  const platform = account?.platform ?? conversation.platform;

  return (
    platform === "instagram" &&
    Boolean(conversation.sourceContext?.mediaUrl) &&
    (conversation.sourceContext?.previewIn === "tagged-in-post" ||
      conversation.sourceContext?.previewIn === "mention-in-caption" ||
      conversation.sourceContext?.previewIn === "mention-in-comment")
  );
}

export function getInboxV2ExpiredReplyEntityLabel(
  messageType: InboxV2Conversation["messageType"]
) {
  return isInboxV2DirectMessageType(messageType) ? "direct message" : "message";
}
